import React, { useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { reduxForm } from "redux-form";
import { EDIT_VERSION_BLADE_FORM } from "forms/list";

import { filteredEditVersionValues } from "selectors/VersionDetails";
import { setVariableSearchTerm } from "reducers/VersionDetails";

import { Button } from "rhombus-react";
import TextEditor from "./TextEditor";
import AssetEditor from "./AssetEditor";
import EmptySection from "widgets/EmptySection";
import { isAfterEffectsCreative, isImposiumCreative, isSocialCreative } from "utils/common";

const editorsByType = {
	Text: TextEditor,
	Image: AssetEditor,
	Video: AssetEditor,
	Richload: AssetEditor,
};

export function shouldUseUnencodedVideos(creative = {}) {
	let isAdStudioCreative = /adstudio/gi.test(creative.origin);
	let isDynamicPrerollCreative = /dynamic_preroll/gi.test(creative.adType);
	const isAfterEffects = isAfterEffectsCreative(creative);
	const isSocial = isSocialCreative(creative);
	const isImposium = isImposiumCreative(creative);

	return [
		isAdStudioCreative && isDynamicPrerollCreative,
		isAfterEffects,
		isSocial,
		isImposium
	].some(cond => cond);
}

function NoSearchResults() {
	const dispatch = useDispatch();
	let emptyProps = {
		className: "empty-variables no-search-results",
		header: "No search results to display",
		message: "Try adjusting your search to find what you are looking for",
		src: "/images/noSearch.svg",
		buttons: [
			<Button key="clear" onClick={() => dispatch(setVariableSearchTerm(""))}>Clear Search</Button>
		],
	};
	return <EmptySection {...emptyProps} />;
}

export function EditVersion(props) {
	const {
		searchAssets,
		handleSubmit,
		onSave,
		versionInfo,
		libraryId,
		saveVersion,
		saveAsVersion,
	} = props;

	const filteredValues = useSelector(state => filteredEditVersionValues(state, EDIT_VERSION_BLADE_FORM));
	const searchTerm = useSelector(state => state.versionDetails.searchTerm);
	const creativeDetails = useSelector(state => state.creativeDetails);
	const isSocial = isSocialCreative(creativeDetails);

	let videoEncodingFilter = 'true'; //string because this is a url query string param
	if (shouldUseUnencodedVideos(creativeDetails)) {
		videoEncodingFilter = 'false';
	}

	const VariableRows = useMemo(() => {
		if (!filteredValues) {
			return null;
		}

		return Object.values(filteredValues).map((value) => {
			let { name, type } = value;
			let Editor = editorsByType[type];
			return (
				<Editor
					key={name}
					value={value}
					form={EDIT_VERSION_BLADE_FORM}
					search={searchAssets}
					libraryId={libraryId}
					versionInfo={versionInfo}
					videoEncodingFilter={videoEncodingFilter}
					saveVersion={saveVersion}
					saveAsVersion={saveAsVersion}
					disabledTextAssets={isSocial}
				/>
			);
		});
	}, [filteredValues]);

	if (searchTerm && (Object.keys(filteredValues)?.length === 0)) {
		return <NoSearchResults />;
	}

	return (
		<div id="edit-version-table" className="edit-version-table" >
			<form className="form" id={EDIT_VERSION_BLADE_FORM} onSubmit={handleSubmit(onSave)}>
				<table>
					<tbody>
						{VariableRows}
					</tbody>
				</table>
			</form>
		</div>
	);
}

export default reduxForm({
	form: EDIT_VERSION_BLADE_FORM,
	destroyOnUnmount: false,
})(EditVersion);

EditVersion.displayName = "EditVersion";
